import { getPathValue } from '@/form-utils/path-utils';
import { whenPathEquals } from './conditions';
import { sharedFieldOrder } from './shared-field-order';
import type { IncidentFieldRule } from './types';

const showFootprint = whenPathEquals('location.scope', 'Location');

function parseLocationFootprint(value: unknown) {
    if (!Array.isArray(value)) return value;
    return value.map((entry) =>
        typeof entry === 'string' ? { locationId: entry } : entry,
    );
}

// Validity gates on the canonical selection, not on a derived site name. The renderer
// writes every checked node's GUID (site, room, rack, …) to `location.locationIds`; a
// region-only or deeper-only selection derives no site but is still a valid Affected-
// Locations selection. Empty `location.locationIds` ⇒ nothing selected ⇒ missing.
function noCanonicalLocationSelected(values: Record<string, unknown>) {
    const locationIds = getPathValue(values, ['location', 'locationIds']);
    return !Array.isArray(locationIds) || locationIds.length === 0;
}

// Top-level `locationFootprint` to match the existing convention used by
// service-request, visit-form, access-card, cross-connect, and the customer-SD
// incident form. Kept virtual (`requireSchemaField: false`): it is UI sugar over the
// real `location.locationIds` DTO field (added by sp-pzw.1) — the renderer projects the
// checked tree into `location.locationIds` (canonical) and `location.sites` (legacy).
export const locationFootprintFieldRule: IncidentFieldRule = {
    key: 'locationFootprint',
    label: 'Affected location footprint',
    description: 'Select the affected site or any deeper locations within it.',
    kind: 'multiselect',
    order: sharedFieldOrder.locationFootprint,
    visibleWhen: showFootprint,
    requiredWhen: showFootprint,
    missingWhen: noCanonicalLocationSelected,
    parse: parseLocationFootprint,
    requireSchemaField: false,
};
